import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Music2, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const isLabelPortal = location.pathname.includes('/label');
    const backgroundImage = isLabelPortal
        ? 'https://images.unsplash.com/photo-1758550445803-2c4046a49437?crop=entropy&cs=srgb&fm=jpg&q=85'
        : 'https://images.unsplash.com/photo-1675292310383-0f4ef53fa3ab?crop=entropy&cs=srgb&fm=jpg&q=85';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const user = await login(email, password);

            // Show the post-login client splash (theme-aware EYP overlay).
            // Dispatch the event so the already-mounted splash component re-triggers,
            // and also set a sessionStorage flag as a backup in case the splash isn't
            // mounted yet on the very first paint of a hard reload.
            try {
                sessionStorage.setItem('elara_show_splash', '1');
                window.dispatchEvent(new Event('elara:show-splash'));
            } catch (_) { /* noop */ }

            // Redirect based on role
            switch (user.role) {
                case 'admin':
                    navigate('/admin/dashboard');
                    break;
                case 'founder':
                    navigate('/admin/founder');
                    break;
                case 'finance':
                    navigate('/admin/finance');
                    break;
                case 'label':
                    navigate('/label/dashboard');
                    break;
                default:
                    navigate('/admin/dashboard');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative flex flex-col items-center justify-center p-4">
            {/* Background */}
            <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${backgroundImage})` }}
            />
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

            {/* Content - Centered vertically and horizontally */}
            <div className="relative z-10 flex flex-col items-center justify-center flex-1 w-full">
                <Card className="w-full max-w-md bg-card/90 backdrop-blur-xl border-border/40 shadow-2xl" data-testid="login-card">
                    <CardHeader className="text-center space-y-4">
                        <div className="mx-auto">
                            <img 
                                src="/branding/elara-logo.png" 
                                alt="ELARA" 
                                className="h-16 w-auto mx-auto"
                            />
                        </div>
                        <div>
                            <CardTitle className="text-2xl font-heading tracking-tight">
                                {isLabelPortal ? 'Label Portal' : 'Operations Portal'}
                            </CardTitle>
                            <CardDescription className="text-muted-foreground">
                                {isLabelPortal 
                                    ? 'Access your revenue reports and manage invoices'
                                    : 'Manage content, labels and revenue data'
                                }
                            </CardDescription>
                        </div>
                    </CardHeader>

                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm" data-testid="login-error">
                                    <AlertCircle className="w-4 h-4 shrink-0" />
                                    {error}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="bg-background/50 border-border/50"
                                    data-testid="email-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="bg-background/50 border-border/50 pr-10"
                                        data-testid="password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full bg-primary hover:bg-primary/90 shadow-[0_0_15px_rgba(124,58,237,0.3)]"
                                disabled={loading}
                                data-testid="login-button"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Signing in...
                                    </>
                                ) : (
                                    'Sign In'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm text-muted-foreground">
                            {isLabelPortal ? (
                                <Link to="/admin/login" className="text-primary hover:underline">
                                    Admin Login
                                </Link>
                            ) : (
                                <Link to="/label/login" className="text-primary hover:underline">
                                    Label Portal Login
                                </Link>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Footer */}
            <div className="relative z-10 pb-4 text-center text-sm text-white/60">
                © 2025 Elara CMS – All Rights Reserved. Elara CMS is a proprietary software product developed and owned by{' '}
                <a 
                    href="https://www.tricona.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                >
                    Tricona
                </a>
                .
            </div>
        </div>
    );
};

export default LoginPage;
